import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { UpdatePatientSchema, Gender } from "@arna-med/shared";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConsultationList } from "@/components/ConsultationList";
import { PatientLifetimeSidebar } from "@/components/PatientLifetimeSidebar";
import { EpisodesSection } from "@/components/EpisodesSection";
import { DiariesSection } from "@/components/DiariesSection";
import { ArrowLeft, Pencil, X, Loader2, Plus } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { usePatient, useUpdatePatient } from "@/hooks/api/usePatients";
import { usePatientDetailConsultations } from "@/hooks/api/useConsultations";

const PAGE_SIZE = 10;

type UpdatePatientForm = z.infer<typeof UpdatePatientSchema>;

export default function PatientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<UpdatePatientForm>({});

  const { data: patient, isLoading: patientLoading } = usePatient(id);
  const { data: consultationsResponse, isLoading: consultationsLoading } =
    usePatientDetailConsultations({ patientId: id, page });
  const updateMutation = useUpdatePatient();

  const consultations = consultationsResponse?.data ?? [];
  const totalPages = Math.ceil((consultationsResponse?.total ?? 0) / PAGE_SIZE);

  if (patientLoading) {
    return (
      <Layout>
        <Spinner />
      </Layout>
    );
  }

  if (!patient) {
    return (
      <Layout>
        <p>{t("common.noResults")}</p>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-6 flex gap-6">
      <div className="flex-1 min-w-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/patients")}
          className="gap-2 mb-4 -ml-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("patients.title")}
        </Button>

        {/* Patient info card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">{patient.name}</CardTitle>
              <div className="flex items-center gap-2">
                {!isEditing && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => navigate(`/consultation/new?patientId=${id}`)}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {t("patients.startConsultation")}
                  </Button>
                )}
                {!isEditing && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setIsEditing(true);
                      setEditForm({});
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {isEditing && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setIsEditing(false);
                      setEditForm({});
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  updateMutation.mutate(
                    { id: id!, data: editForm },
                    {
                      onSuccess: () => {
                        setIsEditing(false);
                        setEditForm({});
                      },
                    },
                  );
                }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                <div className="space-y-2">
                  <Label>{t("patients.fullName")}</Label>
                  <Input
                    value={editForm.name ?? patient.name}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("patients.personalNumber")}</Label>
                  <Input
                    value={editForm.personalNumber ?? patient.personalNumber}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        personalNumber: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("patients.gender")}</Label>
                  <select
                    value={editForm.gender ?? patient.gender}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        gender: e.target.value as Gender,
                      }))
                    }
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value={Gender.Male}>{t("patients.male")}</option>
                    <option value={Gender.Female}>
                      {t("patients.female")}
                    </option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>{t("patients.phone")}</Label>
                  <Input
                    type="tel"
                    value={editForm.phone ?? patient.phone ?? ""}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        phone: e.target.value || undefined,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("patients.email")}</Label>
                  <Input
                    type="email"
                    value={editForm.email ?? patient.email ?? ""}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        email: e.target.value || undefined,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("patients.dateOfBirth")}</Label>
                  <Input
                    type="date"
                    value={editForm.dateOfBirth ?? patient.dateOfBirth ?? ""}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        dateOfBirth: e.target.value || undefined,
                      }))
                    }
                  />
                </div>
                <div className="flex items-end justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setEditForm({});
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={updateMutation.isPending}>
                    {updateMutation.isPending && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    {t("common.save")}
                  </Button>
                </div>
              </form>
            ) : (
              <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <div>
                  <dt className="text-muted-foreground">
                    {t("patients.personalNumber")}
                  </dt>
                  <dd className="font-medium">{patient.personalNumber}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("patients.gender")}
                  </dt>
                  <dd className="font-medium">
                    {t(`patients.${patient.gender}`)}
                  </dd>
                </div>
                {patient.phone && (
                  <div>
                    <dt className="text-muted-foreground">
                      {t("patients.phone")}
                    </dt>
                    <dd className="font-medium">{patient.phone}</dd>
                  </div>
                )}
                {patient.email && (
                  <div>
                    <dt className="text-muted-foreground">
                      {t("patients.email")}
                    </dt>
                    <dd className="font-medium">{patient.email}</dd>
                  </div>
                )}
                {patient.dateOfBirth && (
                  <div>
                    <dt className="text-muted-foreground">
                      {t("patients.dateOfBirth")}
                    </dt>
                    <dd className="font-medium">{patient.dateOfBirth}</dd>
                  </div>
                )}
              </dl>
            )}
          </CardContent>
        </Card>

        {/* Episodes */}
        <div className="mt-6">
          <EpisodesSection patientId={id!} />
        </div>

        {/* Diaries */}
        <div className="mt-6">
          <DiariesSection patientId={id!} />
        </div>

        {/* Consultation history */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-3">
            {t("patients.consultationHistory")}
          </h2>
          <ConsultationList
            consultations={consultations}
            isLoading={consultationsLoading}
            page={page}
            totalPages={totalPages}
            totalItems={consultationsResponse?.total ?? 0}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            showPatient={false}
            emptyMessage={t("patients.noConsultationHistory")}
          />
        </div>
      </div>

      {/* Lifetime sidebar */}
      <div className="w-72 shrink-0">
        <div className="sticky top-4 border rounded-lg p-4 bg-card">
          <PatientLifetimeSidebar patientId={id!} patientName={patient.name} />
        </div>
      </div>
      </div>
    </Layout>
  );
}
